/**
 * Premios y ventajas (plan 2.1 §2): dar a uno o varios usuarios envío gratis o protección gratis
 * entre dos fechas (premios de concursos, compensaciones, ofertas del Club). El servidor valida
 * (admin-grantPerks), audita y solo las aplica en el checkout con el interruptor «Premios y
 * ventajas» encendido (o en «solo admins» para quien es admin).
 */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { CURRENCIES, CURRENCY_MINOR_DIGITS, type Currency } from '@nuvvu/shared';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, money, muted, Notice, str, Table, td, when } from '../lib/ui.js';

type Doc = Record<string, unknown>;
type GrantType = 'free_shipping' | 'free_protection' | 'promo_credit';
type Source = 'contest' | 'club' | 'admin' | 'compensation';

const TYPE_LABEL: Record<string, string> = {
  free_shipping: 'Envío gratis',
  free_protection: 'Protección gratis',
  promo_credit: 'Saldo de premio',
};
const SOURCE_LABEL: Record<Source, string> = {
  contest: 'Concurso / sorteo',
  club: 'Club nuvvu',
  admin: 'Regalo del equipo',
  compensation: 'Compensación por un problema',
};

/** «2026-12-31» (hora de Madrid del navegador) → milisegundos al empezar ese día. */
const dayStart = (d: string): number => new Date(`${d}T00:00:00`).getTime();
const DAY = 86_400_000;

interface Form {
  users: string;
  types: Record<GrantType, boolean>;
  source: Source;
  label: string;
  from: string;
  to: string;
  batch: string;
  reason: string;
  /** Saldo de premio: importe en unidades (200 = 200 €; 73000 = 73.000 Ft) y moneda. */
  amount: string;
  currency: Currency;
}
const blank = (): Form => ({
  users: '',
  types: { free_shipping: true, free_protection: false, promo_credit: false },
  amount: '',
  currency: 'EUR',
  source: 'contest',
  label: '',
  from: '2026-12-01',
  to: '2026-12-31',
  batch: '',
  reason: '',
});

export function PerksPage(): ReactElement {
  const [rows, setRows] = useState<Doc[]>([]);
  const [form, setForm] = useState<Form>(blank);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = (): void => {
    getDocs(query(collection(db, 'userPerks'), orderBy('created_at', 'desc'), limit(300)))
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  useEffect(load, []);

  const set = <K extends keyof Form>(k: K, v: Form[K]): void => {
    setForm((f) => ({ ...f, [k]: v }));
  };

  const grant = async (): Promise<void> => {
    const users = form.users
      .split(/[\s,;]+/)
      .map((u) => u.trim())
      .filter(Boolean);
    const types = (Object.keys(form.types) as GrantType[]).filter((t) => form.types[t]);
    const problem =
      users.length === 0
        ? 'Escribe al menos un usuario (@usuario o id).'
        : types.length === 0
          ? 'Elige al menos una ventaja.'
          : !form.label.trim()
            ? 'Falta el texto que verá el usuario.'
            : !form.from || !form.to || form.to < form.from
              ? 'Revisa las fechas: «hasta» no puede ser antes que «desde».'
              : form.types.promo_credit && !(Number(form.amount.replace(',', '.')) > 0)
                ? 'Escribe el importe del saldo de premio.'
                : null;
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      let granted = 0;
      let skipped = 0;
      const missing = new Set<string>();
      for (const type of types) {
        const r = await call(api.admin.grantPerks, {
          users,
          type,
          source: form.source,
          label: form.label.trim(),
          starts_at: dayStart(form.from),
          // «Hasta» incluido: termina al empezar el día siguiente.
          ends_at: dayStart(form.to) + DAY,
          ...(type === 'promo_credit'
            ? {
                amount: {
                  amount: Math.round(
                    Number(form.amount.replace(',', '.')) *
                      10 ** CURRENCY_MINOR_DIGITS[form.currency],
                  ),
                  currency: form.currency,
                },
              }
            : {}),
          ...(form.batch.trim() ? { batch: form.batch.trim() } : {}),
          ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
        });
        granted += r.granted.length;
        skipped += r.skipped?.length ?? 0;
        r.missing.forEach((m) => missing.add(m));
      }
      setNotice(
        `${granted} ventajas dadas.` +
          (skipped ? ` ${skipped} ya existían en ese lote (no se duplican).` : '') +
          (missing.size ? ` No encontrados: ${[...missing].join(', ')}.` : ''),
      );
      setForm(blank());
      load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = (id: string): void => {
    setBusy(true);
    call(api.admin.revokePerk, { id, reason: 'anulada desde Premios' })
      .then(() => {
        setNotice('Ventaja anulada.');
        load();
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const now = Date.now();
  const stateOf = (r: Doc): string => {
    if (r.revoked_at) return 'anulada';
    const s = (r.starts_at as { toMillis?: () => number } | undefined)?.toMillis?.() ?? 0;
    const e = (r.ends_at as { toMillis?: () => number } | undefined)?.toMillis?.() ?? 0;
    return now < s ? 'programada' : now < e ? 'activa' : 'terminada';
  };

  return (
    <>
      <h1 style={{ margin: 0 }}>Premios y ventajas</h1>
      <p style={muted}>
        Solo se aplican en el checkout con el interruptor «Premios y ventajas» de{' '}
        <Link to="/sistema">Sistema</Link> encendido (o en «Solo admins», para los admins). El saldo
        de premio paga primero en el checkout, no se puede retirar y, si un pedido se cancela o se
        reembolsa, vuelve al saldo de premio.
      </p>
      <Notice text={notice} />
      <Notice text={error} error />
      <section style={{ ...card, display: 'grid', gap: 10, maxWidth: 640 }}>
        <strong>Dar ventaja</strong>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Usuarios (@usuario o id; uno por línea o separados por comas)
          <textarea
            style={{ ...field, minHeight: 70 }}
            value={form.users}
            onChange={(e) => {
              set('users', e.target.value);
            }}
          />
        </label>
        <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
          {(['free_shipping', 'free_protection', 'promo_credit'] as const).map((t) => (
            <label key={t} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={form.types[t]}
                onChange={(e) => {
                  set('types', { ...form.types, [t]: e.target.checked });
                }}
              />
              {TYPE_LABEL[t]}
            </label>
          ))}
        </div>
        {form.types.promo_credit && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Importe del saldo de premio (no se puede retirar; caduca en «hasta»)
              <input
                style={field}
                inputMode="decimal"
                value={form.amount}
                placeholder="200"
                onChange={(e) => {
                  set('amount', e.target.value);
                }}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
              Moneda (solo se usa en compras en esa moneda)
              <select
                style={field}
                value={form.currency}
                onChange={(e) => {
                  set('currency', e.target.value as Currency);
                }}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Desde (incluido)
            <input
              type="date"
              style={field}
              value={form.from}
              onChange={(e) => {
                set('from', e.target.value);
              }}
            />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
            Hasta (incluido)
            <input
              type="date"
              style={field}
              value={form.to}
              onChange={(e) => {
                set('to', e.target.value);
              }}
            />
          </label>
        </div>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Motivo
          <select
            style={field}
            value={form.source}
            onChange={(e) => {
              set('source', e.target.value as Source);
            }}
          >
            {(Object.keys(SOURCE_LABEL) as Source[]).map((s) => (
              <option key={s} value={s}>
                {SOURCE_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Texto para el usuario (en español; se traduce solo)
          <input
            style={field}
            value={form.label}
            placeholder="Premio del concurso de noviembre"
            onChange={(e) => {
              set('label', e.target.value);
            }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Lote (opcional; minúsculas y guiones, p. ej. concurso-2026-11): repetir el mismo lote no
          duplica
          <input
            style={field}
            value={form.batch}
            onChange={(e) => {
              set('batch', e.target.value.toLowerCase());
            }}
          />
        </label>
        <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
          Nota interna (opcional)
          <input
            style={field}
            value={form.reason}
            onChange={(e) => {
              set('reason', e.target.value);
            }}
          />
        </label>
        <button
          type="button"
          style={{ ...btn(true), justifySelf: 'start' }}
          disabled={busy}
          onClick={() => {
            void grant();
          }}
        >
          Dar ventaja
        </button>
      </section>
      <Table head={['Usuario', 'Ventaja', 'Texto', 'Desde', 'Hasta', 'Estado', 'Lote', '']}>
        {rows.map((r) => {
          const st = stateOf(r);
          return (
            <tr
              key={str(r.id)}
              style={{ opacity: st === 'activa' || st === 'programada' ? 1 : 0.55 }}
            >
              <td style={td}>
                <Link to={`/usuarios/${str(r.uid)}`}>{str(r.uid).slice(0, 10)}…</Link>
              </td>
              <td style={td}>
                {TYPE_LABEL[str(r.type)] ?? str(r.type)}
                {r.type === 'promo_credit' && (
                  <span style={muted}>
                    {' '}
                    {money(r.remaining ?? r.amount)} de {money(r.amount)}
                  </span>
                )}
              </td>
              <td style={td}>{str(r.label)}</td>
              <td style={td}>{when(r.starts_at)}</td>
              <td style={td}>{when(r.ends_at)}</td>
              <td style={td}>{st}</td>
              <td style={td}>{str(r.batch) || '—'}</td>
              <td style={td}>
                {st !== 'anulada' && st !== 'terminada' && (
                  <button
                    type="button"
                    style={btn(false, true)}
                    disabled={busy}
                    onClick={() => {
                      revoke(str(r.id));
                    }}
                  >
                    Anular
                  </button>
                )}
              </td>
            </tr>
          );
        })}
        {rows.length === 0 && (
          <tr>
            <td style={td} colSpan={8}>
              Ninguna ventaja todavía.
            </td>
          </tr>
        )}
      </Table>
    </>
  );
}
